import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: November 24, 2025</p>

        <section className="legal-section cookie-settings">
          <h2>Cookie Settings</h2>
          <p>Manage your cookie preferences and control how we use cookies on this website.</p>
          <button className="legal-btn">Manage Cookie Preferences</button>
        </section>

        <div className="legal-content">
          <section className="legal-section">
            <h2>Introduction</h2>
            <p>Welcome to CineClubs.cloud ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at cineclubs.com (the "Platform").</p>
            <p>By accessing or using our Platform, you agree to the terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our Platform.</p>
          </section>

          <section className="legal-section">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Personal Information You Provide</h3>
            <p>When you register, use our services, or interact with our Platform, we may collect:</p>
            <ul>
              <li><strong>Account Information:</strong> Username, email address, password (encrypted), and full name</li>
              <li><strong>Profile Information:</strong> Profile picture, bio, preferences, and settings</li>
              <li><strong>Payment Information:</strong> Payment method details, transaction history, and billing information (processed securely through Stripe and PayPal)</li>
              <li><strong>Communication Data:</strong> Messages, chat logs, and support inquiries</li>
              <li><strong>User-Generated Content:</strong> Comments, reviews, ratings, and uploaded files</li>
            </ul>

            <h3>1.2 Automatically Collected Information</h3>
            <p>When you use our Platform, we automatically collect:</p>
            <ul>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device type</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on pages, navigation paths</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
              <li><strong>Cookies:</strong> Session cookies, preference cookies, and analytics cookies</li>
              <li><strong>WebRTC Data:</strong> Connection metadata for video streaming (streams are peer-to-peer and not stored)</li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <ul>
              <li><strong>Payment Processors:</strong> Transaction confirmation from Stripe and PayPal</li>
              <li><strong>Analytics Providers:</strong> Aggregated usage statistics</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. How We Use Your Information</h2>
            
            <h3>2.1 Service Provision</h3>
            <ul>
              <li>Create and manage your account</li>
              <li>Process payments and transactions</li>
              <li>Enable watch parties, live streaming, and video calls</li>
              <li>Facilitate real-time chat and messaging</li>
              <li>Manage subscriptions and coin-based transactions</li>
              <li>Provide customer support</li>
            </ul>

            <h3>2.2 Platform Improvement</h3>
            <ul>
              <li>Analyze usage patterns and user behavior</li>
              <li>Improve and optimize Platform performance</li>
              <li>Develop new features and functionality</li>
              <li>Fix bugs and resolve technical issues</li>
            </ul>

            <h3>2.3 Communication</h3>
            <ul>
              <li>Send transactional emails (registration, password resets, payment confirmations)</li>
              <li>Respond to inquiries and support requests</li>
              <li>Send service announcements and updates</li>
            </ul>

            <h3>2.4 Security and Legal Compliance</h3>
            <ul>
              <li>Detect and prevent fraud, spam, and abuse</li>
              <li>Enforce our Terms of Service</li>
              <li>Comply with legal obligations</li>
              <li>Protect the rights and safety of our users</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Share Your Information</h2>
            <p className="highlight-red">We do not sell your personal information.</p>
            <p>We may share your information in the following circumstances:</p>
            
            <h3>3.1 With Service Providers</h3>
            <ul>
              <li><strong>Payment Processors:</strong> Stripe and PayPal for payment processing</li>
              <li><strong>Cloud Hosting:</strong> Server providers for data storage</li>
              <li><strong>Email Services:</strong> Transactional email delivery</li>
              <li><strong>Analytics Tools:</strong> Platform usage insights</li>
            </ul>

            <h3>3.2 With Other Users</h3>
            <ul>
              <li>Your username and profile information visible to other users</li>
              <li>Watch party participation and activity</li>
              <li>Comments, reviews, and ratings you post</li>
              <li>Live streaming content you broadcast</li>
            </ul>

            <h3>3.3 For Legal Reasons</h3>
            <ul>
              <li>Comply with legal obligations, court orders, or government requests</li>
              <li>Enforce our Terms of Service</li>
              <li>Protect rights, property, or safety</li>
              <li>Fraud prevention and security investigations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Data Storage and Security</h2>
            
            <h3>4.1 Security Measures</h3>
            <ul>
              <li><strong>Encryption:</strong> Passwords hashed using bcrypt; SSL/TLS for data in transit</li>
              <li><strong>Access Controls:</strong> Restricted access on need-to-know basis</li>
              <li><strong>Secure Servers:</strong> Regular security updates</li>
              <li><strong>Session Management:</strong> Automatic logout after inactivity</li>
              <li><strong>Security Monitoring:</strong> Regular audits and assessments</li>
            </ul>

            <h3>4.2 Data Retention</h3>
            <ul>
              <li><strong>Account Data:</strong> Retained while active and reasonable period after deletion</li>
              <li><strong>Transaction Records:</strong> Retained for at least 7 years</li>
              <li><strong>Logs and Analytics:</strong> Retained for up to 2 years</li>
              <li><strong>Deleted Accounts:</strong> Personal information deleted within 90 days</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Your Rights and Choices</h2>
            
            <h3>5.1 Access and Control</h3>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct information through account settings</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Data Portability:</strong> Receive your data in machine-readable format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
            </ul>

            <h3>5.2 Cookie Preferences</h3>
            <p>You can control cookies through:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for functionality (cannot be disabled)</li>
              <li><strong>Analytics Cookies:</strong> Can be disabled through browser settings</li>
              <li><strong>Preference Cookies:</strong> Managed through account settings</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Children's Privacy</h2>
            <p>Our Platform is not intended for users under the age of 13 (or 16 in the European Union). We do not knowingly collect personal information from children. If we discover that we have collected information from a child, we will delete it immediately.</p>
          </section>

          <section className="legal-section">
            <h2>7. Third-Party Links and Services</h2>
            <p>Our Platform may contain links to third-party websites. We are not responsible for their privacy practices.</p>
            
            <h3>Payment Processors</h3>
            <ul>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
              <li><strong>PayPal:</strong> <a href="https://www.paypal.com/privacy" target="_blank" rel="noopener noreferrer">PayPal Privacy Policy</a></li>
            </ul>

            <h3>WebRTC Technology</h3>
            <p>Our video streaming and watch party features use WebRTC technology for peer-to-peer connections. Audio/video streams are transmitted directly between users and are not stored on our servers.</p>
          </section>

          <section className="legal-section">
            <h2>8. International Users and Legal Rights</h2>
            
            <h3>8.1 European Union Users (GDPR)</h3>
            <p>If you are in the EU, you have additional rights:</p>
            <ul>
              <li>Right to object to data processing</li>
              <li>Right to restrict processing</li>
              <li>Right to lodge a complaint with supervisory authority</li>
              <li>Right to withdraw consent at any time</li>
            </ul>

            <h3>8.2 California Residents (CCPA)</h3>
            <p>California residents have specific rights:</p>
            <ul>
              <li><strong>Right to Know:</strong> Information about data we collect</li>
              <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
              <li><strong>Right to Opt-Out:</strong> We do not sell personal information</li>
              <li><strong>Right to Non-Discrimination:</strong> No discrimination for exercising rights</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Specific Feature Privacy Details</h2>
            
            <h3>9.1 Watch Parties</h3>
            <ul>
              <li>Participation information visible to party members</li>
              <li>Chat messages stored temporarily for moderation</li>
              <li>Video synchronization data transmitted through servers</li>
            </ul>

            <h3>9.2 Live Streaming</h3>
            <ul>
              <li>Stream metadata stored</li>
              <li>Content transmitted using WebRTC (peer-to-peer)</li>
              <li>Viewer counts and analytics collected</li>
            </ul>

            <h3>9.3 Video Calls</h3>
            <ul>
              <li>Connection data facilitated through signaling server</li>
              <li>Audio/video content is peer-to-peer (not recorded or stored)</li>
              <li>Call metadata may be logged</li>
            </ul>

            <h3>9.4 Coin and Subscription System</h3>
            <ul>
              <li>Transaction history stored for accounting purposes</li>
              <li>Payment info processed by third parties (not stored on our servers)</li>
              <li>Subscription status tracked</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
            <ul>
              <li>Posting the updated policy with a new "Last Updated" date</li>
              <li>Sending email notification to registered users</li>
              <li>Displaying a prominent notice on our Platform</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Data Breach Notification</h2>
            <p>In the event of a data breach, we will:</p>
            <ul>
              <li>Notify affected users within 72 hours</li>
              <li>Provide information about the nature of the breach</li>
              <li>Outline steps we are taking to address it</li>
              <li>Recommend protective actions</li>
              <li>Notify relevant authorities as required</li>
            </ul>
          </section>

          <section className="legal-section contact-section">
            <h2>12. Contact Us</h2>
            <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:privacy@cineclubs.com">privacy@cineclubs.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@cineclubs.com">support@cineclubs.com</a></p>
              <p><strong>Response Time:</strong> We will respond to privacy inquiries within 30 days</p>
            </div>
          </section>

          <section className="legal-section">
            <h2>13. Your Responsibilities</h2>
            <p>To help protect your privacy:</p>
            <ul>
              <li>Keep your password secure and don't share it</li>
              <li>Use strong passwords</li>
              <li>Review your privacy settings regularly</li>
              <li>Be cautious sharing personal information in public areas</li>
              <li>Logout from shared or public devices</li>
              <li>Report suspicious activity immediately</li>
            </ul>
          </section>

          <section className="legal-section consent-box">
            <h2>Consent and Acknowledgment</h2>
            <p>By using CineClubs.cloud, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with any part of this policy, please discontinue use of our Platform immediately.</p>
          </section>

          <div className="legal-footer">
            <p>For the most current version of our Privacy Policy, please visit: <Link to="/privacy-policy">https://cineclubs.com/privacy-policy</Link></p>
            <p><strong>Thank you for trusting CineClubs.cloud with your information.</strong></p>
          </div>
        </div>

        <div className="legal-back">
          <Link to="/">
            <i className="fas fa-arrow-left"></i>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
